import { List, useTable, CreateButton } from '@refinedev/antd';
import { Table, Select, Space, Tag, Button, DatePicker } from 'antd';
import { EyeOutlined, EditOutlined, ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

const { RangePicker } = DatePicker;

interface Trip {
  id: number;
  tripType: 'INTERCITY_TAXI' | 'INTERREGION_TAXI' | 'POSTAGE' | 'CARGO';
  status: string;
  pickupAddress: string;
  destinationAddress: string;
  finalPrice: number | null;
  priceEstimate: number;
  createdAt: string;
  requestedAt: string;
  client: {
    fullName: string;
    phone: string;
  };
  driver: {
    fullName: string;
    phone: string;
  } | null;
  pickupRegion: { nameUz: string } | null;
  destinationRegion: { nameUz: string } | null;
  pickupDistrict: { nameUz: string } | null;
  destinationDistrict: { nameUz: string } | null;
}

const WAITING_STATUSES = ['REQUESTED', 'ACCEPTED', 'DRIVER_ARRIVED'];

const formatWaitingTime = (requestedAt: string): string => {
  const diff = Date.now() - new Date(requestedAt).getTime();
  if (diff < 0) return '0s';
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}k ${hours}s ${minutes}d`;
  if (hours > 0) return `${hours}s ${minutes}d`;
  if (minutes > 0) return `${minutes}d ${seconds}s`;
  return `${seconds}s`;
};

export const TripList = () => {
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  const [tripTypeFilter, setTripTypeFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<Trip>({
    resource: 'admin/trips',
    syncWithLocation: true,
    filters: {
      permanent: [
        ...(tripTypeFilter ? [{ field: 'tripType', operator: 'eq' as const, value: tripTypeFilter }] : []),
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
      ],
    },
  });

  // Re-render every 10 seconds to update waiting times
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  const tripTypeLabels: Record<string, string> = {
    INTERCITY_TAXI: 'Shahar ichida',
    INTERREGION_TAXI: 'Shaharlararo',
    POSTAGE: 'Pochta',
    CARGO: 'Yuk',
  };

  const tripTypeColors: Record<string, string> = {
    INTERCITY_TAXI: 'blue',
    INTERREGION_TAXI: 'purple',
    POSTAGE: 'orange',
    CARGO: 'cyan',
  };

  const statusLabels: Record<string, string> = {
    REQUESTED: 'So\'ralgan',
    ACCEPTED: 'Qabul qilingan',
    DRIVER_ARRIVED: 'Haydovchi yetib keldi',
    PICKED_UP: 'Olib ketildi',
    ON_THE_WAY: 'Yo\'lda',
    IN_PROGRESS: 'Jarayonda',
    COMPLETED: 'Yakunlangan',
    CANCELLED: 'Bekor qilingan',
    INTERRUPTED: 'To\'xtatilgan',
    NO_SHOW: 'Kelmadi',
  };

  const statusColors: Record<string, string> = {
    REQUESTED: 'orange',
    ACCEPTED: 'blue',
    DRIVER_ARRIVED: 'cyan',
    PICKED_UP: 'geekblue',
    ON_THE_WAY: 'processing',
    IN_PROGRESS: 'processing',
    COMPLETED: 'green',
    CANCELLED: 'red',
    INTERRUPTED: 'volcano',
    NO_SHOW: 'magenta',
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Turi',
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string) => (
        <Tag color={tripTypeColors[type]}>{tripTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: 'Kutish vaqti',
      key: 'waitingTime',
      render: (_: unknown, record: Trip) => {
        const requestedAt = record.requestedAt || record.createdAt;
        if (WAITING_STATUSES.includes(record.status)) {
          return (
            <Tag icon={<ClockCircleOutlined />} color="warning">
              {formatWaitingTime(requestedAt)}
            </Tag>
          );
        }
        if (record.status === 'COMPLETED' || record.status === 'CANCELLED') {
          return <span style={{ color: '#7F8C8D' }}>-</span>;
        }
        return (
          <span style={{ color: '#7F8C8D', fontSize: 12 }}>
            {formatWaitingTime(requestedAt)}
          </span>
        );
      },
    },
    {
      title: 'Marshrut',
      key: 'route',
      render: (_: unknown, record: any) => {
        const pickup = [
          record.pickupRegion?.nameUz,
          record.pickupDistrict?.nameUz,
        ].filter(Boolean).join(', ');
        const dest = [
          record.destinationRegion?.nameUz,
          record.destinationDistrict?.nameUz,
        ].filter(Boolean).join(', ');
        const destCount = record.destinations?.length;
        return (
          <div style={{ fontSize: 12 }}>
            <div><EnvironmentOutlined style={{ color: '#52c41a' }} /> {pickup || record.pickupAddress || '-'}</div>
            <div><EnvironmentOutlined style={{ color: '#f5222d' }} /> {dest || record.destinationAddress || '-'}</div>
            {destCount > 1 && (
              <div style={{ color: '#7F8C8D', marginTop: 2 }}>+{destCount - 1} manzil</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Mijoz',
      dataIndex: 'client',
      key: 'client',
      render: (client: Trip['client']) => (
        <div>
          <div>{client?.fullName || 'Ismi yo\'q'}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{client?.phone}</div>
        </div>
      ),
    },
    {
      title: 'Haydovchi',
      dataIndex: 'driver',
      key: 'driver',
      render: (driver: Trip['driver']) =>
        driver ? (
          <div>
            <div>{driver.fullName || 'Ismi yo\'q'}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{driver.phone}</div>
          </div>
        ) : (
          <span style={{ color: '#7F8C8D' }}>-</span>
        ),
    },
    {
      title: 'Narx',
      dataIndex: 'finalPrice',
      key: 'finalPrice',
      render: (price: number | null, record: Trip) =>
        formatCurrency(price != null && price > 0 ? price : record.priceEstimate),
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}.${month}.${year}`;
      },
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: Trip) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/trips/${record.tripType}/${record.id}`)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/trips/${record.tripType}/${record.id}/edit`)}
          />
        </Space>
      ),
    },
  ];

  return (
    <List
      title="Sayohatlar"
      headerButtons={
        <Space>
          <Select
            placeholder="Turi"
            allowClear
            style={{ width: 140 }}
            value={tripTypeFilter}
            onChange={(value) => setTripTypeFilter(value)}
            options={Object.entries(tripTypeLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            placeholder="Holat"
            allowClear
            style={{ width: 140 }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            options={Object.entries(statusLabels).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <RangePicker />
          <CreateButton>Yangi sayohat</CreateButton>
        </Space>
      }
    >
      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
